import type { ReactNode } from "react";
import { Link } from "react-router";
import { Button, Card, EmptyState } from "@salesapp/ui";
import { useAccess } from "../../shell/AccessProvider";
import { useAuth } from "../../shell/AuthProvider";
import { useCrmHref } from "../../lib/customerUrl";
import { FALLBACK_TABS } from "../../lib/tabPermissions";
import { PLAN_NAV_LABELS } from "../../lib/planPricing";

const TIER_RANK: Record<string, number> = { starter: 0, professional: 1, enterprise: 2 };

/** Screens that are reachable by URL but not listed as tabs. Mirrors the access catalogue. */
const EXTRA_FEATURES: Record<string, { label: string; minTier?: string }> = {
  "crm.calendar": { label: "Calendar", minTier: "enterprise" },
  "crm.marketing": { label: "Marketing", minTier: "enterprise" },
  "crm.mailbox": { label: "Mailbox", minTier: "enterprise" },
};

/** Design and other child capabilities inherit the parent tab's plan and label. */
const IMPLIED_BY: Record<string, string> = {
  "crm.documents.quote.design": "crm.documents",
  "crm.documents.order.design": "crm.documents",
  "crm.documents.invoice.design": "crm.documents",
  "crm.contacts": "crm.companies",
  "crm.activities": "crm.companies",
  "crm.targets": "crm.forecast",
  "crm.workflows": "crm.companies",
  "crm.opportunities.aging": "crm.opportunities",
};

const ROLE_REASONS = new Set([
  "capability_grant_required",
  "role_required",
  "admin_role_required",
  "seat_required",
]);

/** Paid add-ons sit outside every plan: Saleslox switches them on per organisation. */
const ADD_ONS: Record<string, string> = {
  "crm.lead_generator": "LeadGen",
};

function describeFeature(capability: string): { label: string; minTier?: string } {
  const tab = FALLBACK_TABS.find((item) => item.capability === capability);
  if (tab) return { label: tab.label, minTier: tab.min_tier };
  const parent = IMPLIED_BY[capability];
  if (parent) {
    const parentTab = FALLBACK_TABS.find((item) => item.capability === parent);
    if (parentTab) return { label: parentTab.label, minTier: parentTab.min_tier };
  }
  const extra = EXTRA_FEATURES[capability];
  if (extra) return extra;
  const last = capability.split(".").pop() ?? capability;
  return { label: last.replace(/[_-]/g, " ").replace(/^\w/, (c) => c.toUpperCase()) };
}

export function CapabilityGate({
  capability,
  children,
}: {
  capability: string;
  children: ReactNode;
}) {
  const { can, status, capabilities, tier } = useAccess();
  const { session } = useAuth();
  const crmHref = useCrmHref();
  if (status === "loading") {
    return <Card>Checking access…</Card>;
  }
  if (can(capability)) return children;

  const { label, minTier } = describeFeature(capability);
  const entry = capabilities.find((item) => item.capability === capability);

  // Not built yet: this is not a plan or permission problem, so do not suggest an upgrade.
  if (entry && !entry.implemented) {
    return (
      <Card>
        <EmptyState
          title={`${label} is coming soon`}
          description={`${label} is still being built and is not available on any plan yet. We will announce it when it launches.`}
        />
      </Card>
    );
  }

  if (ADD_ONS[capability] && (!entry || ("reason" in entry && entry.reason === "addon_disabled"))) {
    const name = ADD_ONS[capability];
    return (
      <Card>
        <EmptyState
          title={`${name} is a paid add-on`}
          description={`${name} is not part of any plan. Contact Saleslox support to add it to your organisation.`}
        />
      </Card>
    );
  }

  const isAdmin = Boolean(session?.isOrgAdmin);
  const planName = minTier ? PLAN_NAV_LABELS[minTier] ?? minTier : null;
  const currentRank = tier ? TIER_RANK[tier] : undefined;
  const neededRank = minTier ? TIER_RANK[minTier] : undefined;
  const planCoversIt = currentRank !== undefined && neededRank !== undefined && currentRank >= neededRank;
  const reason = entry && "reason" in entry ? entry.reason : undefined;
  const workforceOff = (capability === "timesheet.entries" || capability === "expense.reports")
    && reason === "license_unconfigured"
    && planCoversIt;

  // Enterprise includes these apps, but an admin can switch one off.
  if (workforceOff) {
    return (
      <Card>
        <EmptyState
          title={`${label} is turned off`}
          description={
            isAdmin
              ? `${label} is included on your plan. Turn it on under Settings, App Settings.`
              : `${label} is included on your plan. Ask an organisation admin to turn it on under Settings, App Settings.`
          }
          action={
            isAdmin ? (
              <Link to={crmHref("/settings?tab=app")}>
                <Button variant="primary">App Settings</Button>
              </Link>
            ) : undefined
          }
        />
      </Card>
    );
  }

  // The plan includes it, or the server said the role/seat is what is missing.
  if (planCoversIt || (reason != null && ROLE_REASONS.has(reason))) {
    return (
      <Card>
        <EmptyState
          title={`You do not have access to ${label}`}
          description={
            isAdmin
              ? `Your plan includes ${label}, but your role does not. Change your role under Settings, Users.`
              : `Your plan includes ${label}, but your role does not. Ask an organisation admin to give you a role that includes it.`
          }
        />
      </Card>
    );
  }

  return (
    <Card>
      <EmptyState
        title={planName ? `${label} is on the ${planName} plan` : `${label} is not on your plan`}
        description={
          isAdmin
            ? `Upgrade your plan to turn ${label} on for your organisation.`
            : `Ask an organisation admin to upgrade the plan to use ${label}.`
        }
        action={
          isAdmin ? (
            <Link to={crmHref("/settings?tab=billing")}>
              <Button variant="primary">View plans</Button>
            </Link>
          ) : undefined
        }
      />
    </Card>
  );
}
